import AppScrollContainer from "@/components/AppScrollContainer";
import DiscoverIcon from "@/components/DiscoverIcon";
import Loading from "@/components/Loading";
import PlayerBar from "@/player/PlayerBar";
import "@/styles/globals.css";
import { theme } from "@/theme";
import { UserProvider } from "@auth0/nextjs-auth0/client";
import { ChakraProvider } from "@chakra-ui/react";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AppProps } from "next/app";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { AudioPlayerProvider } from "react-use-audio-player";

// This code is only for TypeScript
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
  }
}

export default function App({ Component, pageProps }: AppProps) {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
          },
        },
      }),
  );

  // window.__TANSTACK_QUERY_CLIENT__ = queryClient;

  const handleStart = () => {
    scrollRef.current?.scrollTo(0, 0);
    setLoading(true);
  };

  const handleComplete = () => {
    setLoading(false);
  };

  useEffect(() => {
    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, [router]);

  return (
    <>
      <ChakraProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={pageProps.dehydratedState}>
            <AudioPlayerProvider>
              <UserProvider>
                <AppScrollContainer>
                  {router.pathname !== "/image-card/[uuid]" &&
                    !router.pathname.startsWith("/embeddable/") &&
                    router.pathname !== "/app-auth" &&
                    router.pathname !== "/sync-user" && <DiscoverIcon />}

                  {loading ? <Loading /> : <Component {...pageProps} />}

                  <PlayerBar />
                </AppScrollContainer>
              </UserProvider>
            </AudioPlayerProvider>
          </HydrationBoundary>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ChakraProvider>
    </>
  );
}
